/**
 * Origin B "หนีจากเหมืองเงาผา": the full chain from the wake in the prison pit to the sect choice, as data
 * (docs/design/2026-10-13-origin-b-chain.md; beats from docs/design/C0_TOWNS_BCD.md §3.5 on the 96 m greybox town-b).
 * Run by systems/originFull.ts. Coordinates are tiles of town-b (greybox, data/layouts/townGrey.ts) or of the 256 m
 * plains (the last two steps, by the stream). `{m:…|f:…}` picks the hero's sex. The masks' order is never named (LORE P12).
 */
import { FERRYMAN, LOG_SPOT, STARTER_HORSE, STARTER_LOG } from './originChains';
import type { FullChainDef, FullStep } from './originFull';
import type { QuestDef } from '../quests';

const Z = 'town-b';
const P = 'plains';
const q = (s: string): string => `og-b-${s}`;

/** Step ids, in order. */
export const OB = {
  wake: q('wake'),
  lantern: q('lantern'),
  three: q('three'),
  hounds: q('hounds'),
  blade: q('blade'),
  rouse: q('rouse'),
  rise: q('rise'),
  duel: q('duel'),
  escort: q('escort'),
  horse: q('horse'),
  gate: q('gate'),
  bamboo: q('bamboo'),
  banners: q('banners'),
} as const;

/** The four sects that recruit at ด่านธงชาด on the stream's east bank (all are joinable schools). */
export const OB_SECTS = ['cinnabar', 'lantern-road', 'bell-stone', 'dim-moon'] as const;

const quest = (
  id: string,
  title: string,
  offer: string,
  remind: string,
  done: string,
  objectives: QuestDef['objectives'],
  cultivation: number,
  gold: number,
  giver?: QuestDef['giver'],
): QuestDef => ({ id, title, offer, remind, done, objectives, rewards: { cultivation, gold }, ...(giver ? { giver } : {}) });

const XIU = 'ป้าซิ่ว';
const LI = 'ครูมีดเฒ่าหลี่';
const TIE = 'อาเถี่ย';

const steps: FullStep[] = [
  {
    zone: Z,
    startAt: [14.5, 14.5],
    guide: { spot: 'pit' },
    spawns: [{ monster: 'sparrer', at: [18.5, 16.5], name: 'ผู้คุมหลุมขัง', sprite: 'maskedMan' }],
    quest: quest(
      OB.wake,
      'มือในโซ่',
      'เจ้าตื่นในหลุมขังของเหมืองเงาผา โซ่ยังคล้องข้อเท้า ฝ่ามือแดงเหมือนถูกไฟลน รอบตัวมีแต่คนที่มีรอยเหมือนกัน · ' +
        `${XIU}กระซิบ: "ผู้คุมมันง้างมือก่อนตีทุกครั้ง เห็นมันง้างเมื่อไรให้ตั้งรับ แล้วค่อยสวนกลับ"`,
      'โจมตีผู้คุมหลุมขังก่อน รอให้มันง้าง แล้วกดตั้งรับ (F) จากนั้นตีจนมันล้ม',
      'ผู้คุมล้มลง พวงกุญแจหลุดจากเอว โซ่ที่ข้อเท้าเปิดออก',
      [
        { type: 'block', count: 1, label: 'ตั้งรับตอนผู้คุมง้างมือ' },
        { type: 'kill', target: 'sparrer', count: 1, label: 'ล้มผู้คุมหลุมขัง' },
      ],
      120,
      10,
      { name: XIU, where: 'ในหลุมขัง' },
    ),
  },
  {
    zone: Z,
    guide: { spot: 'chest' },
    quest: quest(
      OB.lantern,
      'เงาตะเกียง',
      `"ของของเจ้าอยู่ในหีบท้ายค่าย" ${XIU}ชี้ไปทางตะวันออก "ยามถือตะเกียงหันซ้ายทีขวาที มันกระแอมก่อนหันเสมอ เดินตอนมันหันหลัง"`,
      'เดินผ่านแสงตะเกียงไปถึงหีบท้ายค่าย · ยามกระแอมแล้วจะหัน ให้หยุดรอนอกแสง',
      'เปิดหีบได้ กระบี่ของเจ้ายังอยู่ ห่อผ้ายังมีกลิ่นฝนเมื่อคืน',
      [{ type: 'route', target: 'lantern', count: 3, label: 'ผ่านแสงตะเกียงไปถึงหีบ (ถูกเห็น = กลับไปเริ่มใหม่)' }],
      150,
      20,
    ),
  },
  {
    zone: Z,
    guide: { npc: 'pr1' },
    talkLines: {
      pr1: 'ขอบใจ… ข้าเห็นมันเอามือคนวางบนแผ่นศิลาดำ ถ้าศิลาเรืองแสง คนนั้นถูกพาขึ้นเหนือ',
      pr2: 'เมื่อวานศิลาเรืองให้ชายคนหนึ่ง เขาไม่ได้กลับมาอีกเลย',
      pr3: '{m:พ่อหนุ่ม|f:แม่นาง} มือเจ้าแดงกว่าใคร พวกมันต้องเรียกเจ้าก่อนแน่ รีบไปเถอะ',
    },
    quest: quest(
      OB.three,
      'สามคนที่ยังหายใจ',
      'ใกล้ห้องแผ่นศิลามีคนมีรอยถูกล่ามอีกสามคน ใช้พวงกุญแจของผู้คุมไขโซ่ให้พวกเขา แล้วให้ทุกคนหนีไปลานโม่ศิลา',
      'ไขโซ่ให้นักโทษสามคนหน้าห้องแผ่นศิลา (ทางเหนือของค่าย)',
      'ทั้งสามวิ่งลงไปทางลานโม่ศิลา · พวกหน้ากากทดสอบฝ่ามือคนมีรอย แล้วส่งคนที่ศิลาตอบรับขึ้นเหนือ',
      [{ type: 'talk', target: 'prisoner', count: 3, label: 'ไขโซ่ให้นักโทษ' }],
      200,
      20,
    ),
  },
  {
    zone: Z,
    guide: { spot: 'pocket' },
    quest: quest(
      OB.hounds,
      'หมาล่าของผู้คุม',
      'หมาล่าของพวกผู้คุมได้กลิ่นนักโทษที่หนี มันวนอยู่ที่ลานกองตะกรันมุมตะวันออกเฉียงใต้ จัดการมันก่อนมันตามรอยไปถึงลาน · ' +
        'หมามันย่อตัวก่อนกัด ตั้งรับให้ทัน',
      'ลานกองตะกรันอยู่มุมตะวันออกเฉียงใต้ของเมือง · มันย่อตัวก่อนกัด ให้ตั้งรับ (F)',
      'หมาล่าหมดแล้ว ไม่มีใครตามกลิ่นพวกเจ้าได้อีก',
      [
        { type: 'kill', target: 'wolf', count: 3, label: 'หมาล่าของผู้คุม' },
        { type: 'block', count: 2, label: 'ตั้งรับตอนหมาง้างกัด' },
      ],
      300,
      40,
    ),
  },
  {
    zone: Z,
    guide: { npc: 'li' },
    grantQinggong: 'swallow-dash',
    talkLines: {
      li: 'เข้ามาเร็ว! ในอุโมงค์ ดาบยาวคือฝาโลง ขาเร็วต่างหากที่รอด · ย่อเข่า ปล่อยลมหายใจ แล้วพุ่ง นี่คือนกนางแอ่นพุ่งตัว',
    },
    quest: quest(
      OB.blade,
      'ครูมีดในเงา',
      `ยามขึ้นมาตามหาแล้ว ${LI}โบกมือเรียกจากประตูโรงฝึกยุทธ์ทางเหนือของลาน เขาเคยเป็นยามคุ้มทาง ก่อนพวกหน้ากากจะยึดเหมือง`,
      `ไปหา${LI}ที่ประตูโรงฝึกยุทธ์ เหนือลานโม่ศิลา`,
      'ได้วิชาตัวเบา นกนางแอ่นพุ่งตัว (กดพุ่งตัวขณะวิ่ง)',
      [{ type: 'talk', target: 'li', count: 1, label: `ฟัง${LI}สอนวิชาตัวเบา` }],
      200,
      30,
      { name: LI, where: 'ที่โรงฝึกยุทธ์' },
    ),
  },
  {
    zone: Z,
    guide: { spot: 'door1' },
    timer: { seconds: 30, failTo: [61.5, 49.5], failText: 'ไม่ทันเสียแล้ว ยามเปลี่ยนเวร · ตั้งต้นใหม่ที่หน้าโรงฝึกยุทธ์' },
    quest: quest(
      OB.rouse,
      'ปลุกคนแร่',
      `"คนแร่ทั้งเมืองรอสัญญาณอยู่" ${LI}ว่า "เคาะประตูสามบ้านก่อนยามเปลี่ยนเวร ใช้ท่าพุ่งตัวที่ข้าสอน"`,
      'เคาะประตูสามบ้านรอบลาน ให้ทันในเวลา (โรงคลัง → บ้านตะวันตกเฉียงใต้ → บ้านตะวันออกเฉียงใต้)',
      'ประตูทุกบานเปิด คนแร่ถือพลั่วถือค้อนออกมาเต็มลาน',
      [{ type: 'route', target: 'rouse', count: 3, label: 'เคาะประตูบ้านคนแร่ (มีเวลาจำกัด)' }],
      250,
      30,
    ),
  },
  {
    zone: Z,
    guide: { spot: 'road' },
    spawns: [
      { monster: 'masked', at: [46.5, 26.5], name: 'ผู้คุมหน้ากากไม้' },
      { monster: 'masked', at: [50.5, 24.5], name: 'ผู้คุมหน้ากากไม้' },
      { monster: 'masked', at: [48.5, 30.5], name: 'ผู้คุมหน้ากากไม้' },
    ],
    quest: quest(
      OB.rise,
      'ลุกฮือ',
      'ผู้คุมหน้ากากสามคนลงมาขวางถนนเหนือ คนแร่ส่งเสียงหนุนอยู่ข้างหลังเจ้า · พวกมันชอบตั้งรับ ใช้ท่าทำลายตัดการ์ดก่อน',
      'ผู้คุมหน้ากากอยู่บนถนนเหนือลาน · มันตั้งรับให้ใช้ท่าทำลาย มันง้างให้ตั้งรับ',
      'ผู้คุมคนสุดท้ายถอยหนีขึ้นค่าย ลานโม่ศิลาเป็นของคนแร่แล้ว',
      [
        { type: 'kill', target: 'masked', count: 3, label: 'ผู้คุมหน้ากากไม้' },
        { type: 'guardBreak', count: 1, label: 'ท่าทำลายตัดการ์ดผู้คุม' },
      ],
      450,
      60,
    ),
  },
  {
    zone: Z,
    guide: { npc: 'duel' },
    quest: quest(
      OB.duel,
      'นักสู้ประจำเหมือง',
      'ฟางเจิ้นนักสู้ประจำเหมืองยืนขวางทาง "คนมีรอยพาเรื่องมาให้เหมือง ถ้าจะให้ข้าเฝ้าประตูให้ ชนะข้าบนลานก่อน" · ' +
        'คลิกที่เขา แล้วคลิกขวาที่รูป เลือกท้าประลอง',
      'ท้าประลองฟางเจิ้นที่ลานโม่ศิลา แพ้ก็ไม่เสียอะไร ท้าใหม่ได้',
      'ฟางเจิ้นลุกขึ้นปัดฝุ่น "ได้ ข้าเฝ้าประตูให้ ส่วนป้าซิ่วฝากเจ้าพาไปคอกม้า"',
      [{ type: 'duelWin', count: 1, label: 'ชนะประลองฟางเจิ้น' }],
      500,
      80,
      { name: 'ฟางเจิ้น', where: 'ที่ลานโม่ศิลา' },
    ),
  },
  {
    zone: Z,
    guide: { npc: 'xiu' },
    quest: quest(
      OB.escort,
      'พาป้าซิ่วไปคอกม้า',
      `${XIU}เดินช้า ข้อเท้ายังเป็นแผลจากโซ่ พาป้าไปคอกม้าทางตะวันออก ระวังผู้คุมที่ยังซุ่มอยู่`,
      `เดินนำ${XIU}ไปคอกม้าทางตะวันออกของลาน อย่าทิ้งห่าง ป้าจะหยุดรอ`,
      `"ข้าไปต่อไม่ไหวแล้ว เจ้าไปเถิด" ${XIU}นั่งพักที่คอกม้า`,
      [{ type: 'escort', target: 'xiu', count: 1, label: `พา${XIU}ถึงคอกม้า` }],
      350,
      40,
    ),
  },
  {
    zone: Z,
    guide: { npc: 'horsekeeper' },
    grantMount: { npc: 'horsekeeper', mount: STARTER_HORSE },
    talkLines: {
      horsekeeper:
        'ม้าตัวนี้พวกหน้ากากยึดไว้ ข้ายกให้เจ้า กดปุ่มขี่พาหนะเพื่อขึ้น กดอีกครั้งเพื่อลง ขี่ไปลานตะวันออกให้มันคุ้นมือก่อน',
    },
    quest: quest(
      OB.horse,
      'ม้าตัวแรก',
      'ลุงม้าเหมืองซ่อนม้าไว้ตัวหนึ่งในคอก ขอม้าแล้วลองขี่ให้คล่อง เพราะทางออกเดียวคือประตูเหนือ',
      'คุยกับลุงม้าเหมืองที่คอกม้า แล้วขี่ม้าไปที่ลานตะวันออก',
      'ม้ารู้ใจเจ้าแล้ว ม้าตัวนี้เป็นของเจ้า',
      [
        { type: 'talk', target: 'horsekeeper', count: 1, label: 'รับม้าจากลุงม้าเหมือง' },
        { type: 'visit', target: 'horseRide', count: 1, label: 'ขี่ม้าไปลานตะวันออก' },
      ],
      300,
      60,
      { name: 'ลุงม้าเหมือง', where: 'ที่คอกม้า' },
    ),
    ordered: true,
  },
  {
    zone: Z,
    guide: { spot: 'gate' },
    timer: { seconds: 40, failTo: [70.5, 59.5], failText: 'พวกมันปิดประตูทัน! ถอยกลับคอกม้าแล้วควบใหม่' },
    spawns: [
      { monster: 'masked', at: [64.5, 50.5], name: 'ผู้คุมไล่ล่า' },
      { monster: 'masked', at: [74.5, 64.5], name: 'ผู้คุมไล่ล่า' },
    ],
    quest: quest(
      OB.gate,
      'ควบฝ่าประตูเหนือ',
      'เสียงฆ้องจากค่ายดังลั่น พวกมันจะปิดประตูเหนือ ขึ้นม้าแล้วควบไปให้ทัน ฟางเจิ้นรั้งประตูไว้ให้ได้ไม่นาน',
      'ขี่ม้าไปประตูเหนือให้ทันเวลา (ต้องอยู่บนหลังม้า)',
      'ม้าพุ่งผ่านช่องประตู ฟางเจิ้นปิดประตูตามหลัง ถนนลงเขาพาไปทุ่งราบ',
      [{ type: 'visit', target: 'gate', count: 1, label: 'ขี่ม้าฝ่าประตูเหนือ' }],
      350,
      60,
    ),
  },
  {
    zone: P,
    guide: { npc: 'ferryman' },
    grantMount: { npc: 'ferryman', mount: STARTER_LOG },
    spawns: [
      { monster: 'masked', at: [155.5, 124.5], name: 'ยามหน้ากากเฝ้าสะพาน' },
      { monster: 'masked', at: [155.5, 127.5], name: 'ยามหน้ากากเฝ้าสะพาน' },
    ],
    talkLines: {
      ferryman:
        'พวกหน้ากากเฝ้าสะพานอยู่ อย่าเดินข้ามเลย ไม้ไผ่ท่อนนี้ยกให้ เลือกไผ่ลำเดียวเป็นพาหนะ ยืนบนลำแล้วพายลงกลางน้ำ ฝั่งตะวันออกมีธงแดงรออยู่',
    },
    quest: quest(
      OB.bamboo,
      'ไผ่ลำเดียว',
      'ลำธารกลางทุ่งขวางทาง และพวกหน้ากากเฝ้าสะพาน ลุงพายไผ่ริมน้ำมีไม้ไผ่ให้หนึ่งท่อน ยืนบนมันแล้วพายข้ามไปฝั่งตะวันออก',
      'คุยกับลุงพายไผ่ริมลำธาร ขึ้นไม้ไผ่ พายลงกลางน้ำ แล้วขึ้นฝั่งตะวันออก',
      'เท้าแตะฝั่งตะวันออก ธงแดงโบกอยู่ข้างหน้า',
      [
        { type: 'talk', target: 'ferryman', count: 1, label: 'รับไม้ไผ่จากลุงพายไผ่' },
        { type: 'visit', target: 'logMid', count: 1, label: 'ยืนพายไม้ไผ่ลงกลางลำธาร' },
        { type: 'visit', target: 'eastBank', count: 1, label: 'ขึ้นฝั่งตะวันออก' },
      ],
      350,
      60,
      { name: FERRYMAN.name, where: 'ริมลำธารกลางทุ่ง' },
    ),
    ordered: true,
  },
  {
    zone: P,
    guide: { npc: 'qin' },
    quest: quest(
      OB.banners,
      'ธงที่ฝั่งตะวันออก',
      'ฉินเยว่ศิษย์ดาบชาดฟันโซ่ที่ข้อเท้าเจ้าขาดในดาบเดียว "คนมีรอยที่ไม่มีสำนัก คือเหยื่อที่เดินได้" · ' +
        'ที่ด่านนี้มีผู้แทนสี่สำนักกำลังตามล่าพวกหน้ากาก คุยกับแต่ละคนแล้วเลือกทางของเจ้า หรือจะเดินคนเดียวก็ได้',
      'คุยกับผู้แทนสำนักที่ฝั่งตะวันออก แล้วเข้าร่วมสำนัก · หรือบอกอาเถี่ยว่าจะเดินคนเดียว',
      'ทางของเจ้าเริ่มแล้ว · ที่เหมืองเงาผายังมีคนมีรอยอีกมาก และแผ่นศิลาดำยังรอคำตอบ',
      [{ type: 'sect', count: 1, label: 'เข้าร่วมสำนัก หรือเดินคนเดียว' }],
      500,
      100,
    ),
  },
];

export const ORIGIN_B: FullChainDef = {
  origin: 'b',
  steps,
  duelRival: { name: 'ฟางเจิ้น' },
  npcs: [
    {
      id: 'xiu',
      zone: Z,
      name: XIU,
      title: 'นักโทษมีรอย',
      sprite: 'farmerAunt',
      at: [13.5, 16.5],
      idle: ['ข้าอยู่ที่นี่มาสามเดือน เห็นคนมาแล้วก็ไป', 'ยังไม่ทันเห็นหน้าเจ้าชัด แต่ข้าว่าเจ้ารอดได้'],
      after: { step: OB.three, to: [45.5, 55.5] },
    },
    { id: 'pr1', talkAs: 'prisoner', zone: Z, name: 'ชายแบกแร่', title: 'นักโทษถูกล่าม', sprite: 'stationMaster', at: [40.5, 12.5], idle: ['ข้าจะไปรอที่ลาน'], after: { step: OB.three, to: [41.5, 58.5] } },
    { id: 'pr2', talkAs: 'prisoner', zone: Z, name: 'หญิงร่อนแร่', title: 'นักโทษถูกล่าม', sprite: 'townGirl', at: [44.5, 9.5], idle: ['ขอบใจ ข้าจะไม่ลืม'], after: { step: OB.three, to: [52.5, 60.5] } },
    { id: 'pr3', talkAs: 'prisoner', zone: Z, name: 'เด็กคัดหิน', title: 'นักโทษถูกล่าม', sprite: 'townKid', at: [50.5, 12.5], idle: ['ข้าวิ่งเร็วนะ!'], after: { step: OB.three, to: [55.5, 57.5] } },
    {
      id: 'li',
      zone: Z,
      name: LI,
      title: 'ครูโรงฝึกยุทธ์',
      sprite: 'watchman',
      at: [61.5, 47.5],
      idle: ['ในอุโมงค์ ขาเร็วรอดกว่าดาบยาว', 'ฝึกพุ่งตัวให้คล่อง แล้วค่อยฝึกท่ามีด'],
    },
    {
      id: 'horsekeeper',
      zone: Z,
      name: 'ลุงม้าเหมือง',
      title: 'คนเลี้ยงม้าเหมือง',
      sprite: 'stationMaster',
      at: [70.5, 57.5],
      idle: ['ม้าเหมืองขาแข็ง ขึ้นเขาไม่เหนื่อย'],
    },
    // The stream's east bank on the plains (ด่านธงชาด): the four envoys and the miner lad.
    { id: 'qin', zone: P, from: OB.bamboo, name: 'ฉินเยว่', title: 'ศิษย์ดาบชาด · หน่วยลาดตระเวน', sprite: 'bloodMaster', at: [154.5, 135.5], school: 'cinnabar', idle: ['ดาบชาดไม่รอให้ใครมาช่วย'] },
    { id: 'shen', zone: P, from: OB.bamboo, name: 'ผู้คุมโคมเสิ่นป๋อ', title: 'ทางโคมเหล็ก', sprite: 'guardOfficer', at: [156.5, 134.5], school: 'lantern-road', idle: ['ทางค้าต้องมีคนเฝ้าโคม'] },
    { id: 'wu', zone: P, from: OB.bamboo, name: 'ช่างตอกหินอู๋ซาน', title: 'หินระฆัง', sprite: 'monk', at: [158.5, 135.5], school: 'bell-stone', idle: ['หินที่เจ้าขุดมา ข้าเคยตอกเป็นระฆัง'] },
    { id: 'yao', zone: P, from: OB.bamboo, name: 'เหยาซิน', title: 'จันทร์มัว', sprite: 'blissLady', at: [155.5, 139.5], school: 'dim-moon', idle: ['คนที่เคยถูกล่าม มองคนออกเร็วกว่าใคร'] },
    {
      id: 'tie',
      zone: P,
      from: OB.bamboo,
      name: TIE,
      title: 'คนแร่หนุ่ม · หนีมาด้วยกัน',
      sprite: 'townKid',
      at: [157.5, 139.5],
      wanderer: true,
      idle: ['สักวันพวกเราคนเหมืองจะมีที่ของเราเอง พี่น้องที่เคยแบกโซ่ด้วยกัน'],
    },
  ],
  spots: {
    pit: { at: [18.5, 16.5], r: 4 },
    chest: { at: [43.5, 22.5], r: 2 },
    pocket: { at: [76, 77], r: 10 },
    door1: { at: [35.5, 47.5], r: 2 },
    road: { at: [48.5, 27.5], r: 6 },
    horseRide: { at: [82.5, 40.5], r: 5, mount: STARTER_HORSE },
    gate: { at: [48.5, 4.5], r: 5, mount: STARTER_HORSE },
    logMid: { ...LOG_SPOT, mount: STARTER_LOG },
    eastBank: { at: [156.5, 137.5], r: 3 },
  },
  routes: {
    lantern: {
      id: 'lantern',
      points: [
        { at: [17.5, 22.5], r: 1.5 },
        { at: [30.5, 22.5], r: 1.2 },
        { at: [43.5, 22.5], r: 1.5 },
      ],
      watchers: [
        { at: [24.5, 20.5], range: 5, band: 3, period: 3, warn: 0.8, startFacing: 1 },
        { at: [36.5, 20.5], range: 5, band: 3, period: 3, warn: 0.8, startFacing: -1 },
      ],
      resetTo: [16.5, 22.5],
      failText: 'ยามเห็นเจ้าแล้ว! "ใครน่ะ!" · ถอยกลับไปตั้งต้นที่ปากหลุม',
    },
    rouse: {
      id: 'rouse',
      points: [
        { at: [35.5, 47.5], r: 2 },
        { at: [34.5, 64.5], r: 2 },
        { at: [62.5, 64.5], r: 2 },
      ],
      resetTo: [61.5, 49.5],
      failText: 'ไม่ทันเสียแล้ว ยามเปลี่ยนเวร · ตั้งต้นใหม่ที่หน้าโรงฝึกยุทธ์',
    },
  },
  escorts: {
    xiu: {
      id: 'xiu',
      npc: 'xiu',
      path: [
        [45.5, 55.5],
        [56.5, 57.5],
        [64.5, 58.5],
        [68.5, 59.5],
      ],
      speed: 1.6,
      leadRange: 8,
      waves: [{ atPathIndex: 2, monster: 'masked', n: 2 }],
      waitText: 'รอข้าด้วย… ขาข้าไม่ไหว',
    },
  },
  lines: {
    wandererPick: 'เดินคนเดียวก็ได้ แต่จำไว้ พวกหน้ากากชอบคนที่อยู่ลำพัง ถ้าเหนื่อยเมื่อไร กลับมาหาพวกเราที่เหมือง',
    joined: 'ดี มีคนยืนข้างเจ้าแล้ว',
    chosenAlready: 'เจ้าเลือกทางแล้ว ข้ายินดีด้วย',
    allDone: 'ไปเถอะ ยุทธภพกว้างกว่าหลุมเหมืองนัก',
  },
};
